import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Plus, ChevronDown, ChevronUp, Send, Pencil, Trash2, X } from "lucide-react";
import { useAuthContext } from "@/components/AuthProvider";
import { useTermsOfUse, Term } from "@/hooks/useTermsOfUse";
import { useCreateTerm } from "@/hooks/useCreateTerm";
import { usePublishTerm } from "@/hooks/usePublishTerm";
import { useDiscardTerm } from "@/hooks/useDiscardTerm";
import { AddPoliticaDialog } from "@/components/politicas/AddPoliticaDialog";
import { EditTermDialog } from "@/components/politicas/EditTermDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

const AUTHORIZED_USER_IDS = [
  '8ae8de44-7553-41de-9781-c2ce054b2af4', // marianalima@musa.co
  '2b3982c5-6a81-4b00-99ca-92d5aace8a0c', // joana@musa.co
  '40b6edf3-f8cb-4730-9363-7e3ee4956adc', // isabelle.soares@musa.co
];

export interface TermoContent {
  id: string;
  title: string;
  content: {
    html: string;
  };
  created_at?: string;
  archived_at?: string;
}

const TermosDeUsoAdmin = () => {
  const { profile, isLoading: isAuthLoading } = useAuthContext();
  const { data: termsData, isLoading } = useTermsOfUse();
  const createTermMutation = useCreateTerm();
  const publishTermMutation = usePublishTerm();
  const discardTermMutation = useDiscardTerm();
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [viewingTermo, setViewingTermo] = useState<TermoContent | null>(null);
  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  const [discardingTermId, setDiscardingTermId] = useState<string | null>(null);
  const [isCurrentTermOpen, setIsCurrentTermOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [publishingTermId, setPublishingTermId] = useState<string | null>(null);
  
  const ITEMS_PER_PAGE = 5;

  const isInternal = profile?.is_internal || false;
  const isAuthorized = profile?.user_id && AUTHORIZED_USER_IDS.includes(profile.user_id);

  // Redirect unauthorized users
  if (!isAuthLoading && !isAuthorized) {
    return <Navigate to="/unauthorized" replace />;
  }

  const handleAddTermo = async (newTermo: Omit<TermoContent, "id">) => {
    if (!profile?.user_id) return;
    
    await createTermMutation.mutateAsync({
      content: newTermo.content.html,
      createdByUserId: profile.user_id,
      createdByUserName: profile.user_name || profile.email
    });
    
    setShowAddDialog(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const userId = profile?.user_id || '';
  const userName = profile?.user_name || profile?.email || '';

  const handlePublishTerm = async () => {
    if (!publishingTermId) return;
    
    await publishTermMutation.mutateAsync({ termId: publishingTermId, userId, userName });
    setPublishingTermId(null);
  };

  const handleDiscardTerm = async () => {
    if (!discardingTermId) return;
    
    await discardTermMutation.mutateAsync({ termId: discardingTermId, userId, userName });
    setDiscardingTermId(null);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const currentTermo = termsData?.activeTerm;
  const pendingTerms = termsData?.pendingTerms || [];
  const historico = termsData?.history || [];
  
  const totalPages = Math.ceil(historico.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedHistorico = historico.slice(startIndex, endIndex);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Termos de Uso</h1>
          <p className="text-muted-foreground mt-1">
            Versão atual dos termos de uso do Portal MUSA
          </p>
        </div>
        {isInternal && (
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Versão
          </Button>
        )}
      </div>

      {/* Termos Pendentes de Publicação */}
      {isInternal && pendingTerms.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">Termos Pendentes de Publicação</h2>
            <Alert className="mb-4">
              <AlertDescription>
                Estes termos foram criados mas ainda não foram publicados. Após revisão, você pode dispará-los para todos os usuários.
              </AlertDescription>
            </Alert>
            <div className="space-y-3">
              {pendingTerms.map((termo) => {
                const lastLog = termo.logs?.[0];
                const actionLabels: Record<string, string> = {
                  create: 'Criado',
                  edit: 'Editado',
                  discard: 'Descartado',
                  publish: 'Publicado'
                };
                
                return (
                  <div 
                    key={termo.id} 
                    className="flex items-center justify-between py-3 px-4 border rounded-lg bg-muted/30"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        Criado em {formatDate(termo.created_at || '')}
                      </p>
                      {termo.created_by_user_name && (
                        <p className="text-xs text-muted-foreground">
                          Criado por: {termo.created_by_user_name}
                        </p>
                      )}
                      {lastLog && lastLog.action !== 'create' && (
                        <p className="text-xs text-muted-foreground">
                          Última ação: <span className="font-medium">{actionLabels[lastLog.action]}</span>
                          {lastLog.performed_by_user_name && (
                            <span> por {lastLog.performed_by_user_name}</span>
                          )}
                          <span> em {formatDate(lastLog.performed_at)}</span>
                        </p>
                      )}
                    </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setViewingTermo({
                        id: termo.id,
                        title: "Termo Pendente",
                        content: { html: termo.content },
                        created_at: termo.created_at
                      })}
                    >
                      Ver Conteúdo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingTerm(termo)}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDiscardingTermId(termo.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Descartar
                    </Button>
                    <Button
                      onClick={() => setPublishingTermId(termo.id)}
                      disabled={publishTermMutation.isPending}
                      size="sm"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      {publishTermMutation.isPending ? 'Publicando...' : 'Disparar'}
                    </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Termo Atual */}
      {currentTermo ? (
        <Card>
          <Collapsible open={isCurrentTermOpen} onOpenChange={setIsCurrentTermOpen}>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Versão Atual</h2>
                  <p className="text-sm text-muted-foreground">
                    Publicado em {formatDate(currentTermo.created_at || '')}
                  </p>
                </div>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm">
                    {isCurrentTermOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                <div
                  className="prose prose-sm max-w-none mt-4"
                  dangerouslySetInnerHTML={{ __html: currentTermo.content }}
                />
              </CollapsibleContent>
            </CardContent>
          </Collapsible>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground py-8">
              Nenhum termo de uso foi publicado ainda.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Termos */}
      {isInternal && historico.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">Histórico de Versões</h2>
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {paginatedHistorico.map((termo) => {
                  const lastLog = termo.logs?.[0];
                  const actionLabels: Record<string, string> = {
                    create: 'Criado',
                    edit: 'Editado',
                    discard: 'Descartado',
                    publish: 'Publicado'
                  };
                  
                  return (
                    <div 
                      key={termo.id} 
                      className="flex items-center justify-between py-3 border-b last:border-b-0"
                    >
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">
                          Publicado em {formatDate(termo.created_at || '')}
                          {termo.updated_at && (
                            <span> • Arquivado em {formatDate(termo.updated_at)}</span>
                          )}
                        </p>
                        {lastLog && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Última ação: <span className="font-medium">{actionLabels[lastLog.action] || lastLog.action}</span>
                            {lastLog.performed_by_user_name && (
                              <span> por {lastLog.performed_by_user_name}</span>
                            )}
                            <span> em {formatDate(lastLog.performed_at)}</span>
                          </p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setViewingTermo({
                          id: termo.id,
                          title: "Versão Arquivada",
                          content: { html: termo.content },
                          created_at: termo.created_at,
                          archived_at: termo.updated_at || undefined
                        })}
                      >
                        Ver Conteúdo
                      </Button>
                    </div>
                  );
                })}
              </div>
              
              {totalPages > 1 && (
                <Pagination className="mt-6">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog para adicionar novo termo */}
      {showAddDialog && (
        <AddPoliticaDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          onAdd={handleAddTermo}
          nextOrderIndex={0}
        />
      )}

      {/* Dialog para editar termo pendente */}
      {editingTerm && (
        <EditTermDialog
          open={!!editingTerm}
          onClose={() => setEditingTerm(null)}
          termId={editingTerm.id}
          initialContent={editingTerm.content}
        />
      )}

      {/* Dialog para visualizar termo do histórico */}
      <Dialog open={!!viewingTermo} onOpenChange={() => setViewingTermo(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader className="pr-8">
            <DialogTitle>{viewingTermo?.title}</DialogTitle>
            {viewingTermo?.created_at && (
              <p className="text-sm text-muted-foreground">
                Publicado em {formatDate(viewingTermo.created_at)}
                {viewingTermo.archived_at && ` • Arquivado em ${formatDate(viewingTermo.archived_at)}`}
              </p>
            )}
          </DialogHeader>
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
          {viewingTermo && (
            <div
              className="prose prose-sm max-w-none mt-4 break-words overflow-wrap-anywhere"
              dangerouslySetInnerHTML={{ __html: viewingTermo.content.html }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação para publicar termo */}
      <AlertDialog open={!!publishingTermId} onOpenChange={(open) => !open && setPublishingTermId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Publicação do Termo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja publicar e disparar este termo? Esta ação irá:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Arquivar o termo atual</li>
                <li>Publicar este novo termo</li>
                <li>Enviar notificação para todos os usuários</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handlePublishTerm} disabled={publishTermMutation.isPending}>
              {publishTermMutation.isPending ? 'Publicando...' : 'Confirmar e Disparar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de confirmação para descartar termo */}
      <AlertDialog open={!!discardingTermId} onOpenChange={(open) => !open && setDiscardingTermId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar Termo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja descartar este termo pendente? O termo será movido para o histórico de versões arquivadas e não poderá ser publicado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDiscardTerm} 
              disabled={discardTermMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {discardTermMutation.isPending ? 'Descartando...' : 'Descartar Termo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TermosDeUsoAdmin;
